/**
 * Level17Scene - 关卡17：引力透镜
 * @description 教学目标：理解引力透镜效应
 * 核心公式：光线偏折角 δ ≈ 4GM/(rc²) = 2Rs/r
 * 爱因斯坦环半径：r_E = √(4GMd/c²)
 * 使用 LensingRenderer 显示光线弯曲效果
 */

import Phaser from 'phaser';
import { Vec2 } from 'planck';
import type { LevelConfig } from '@/levels/LevelConfig.ts';
import { TutorialUI } from '@/ui/TutorialUI.ts';
import { getTutorial } from '@/data/tutorials.ts';
import { getGameState } from '@/core/GameState.ts';
import { Blackhole, DEFAULT_BLACKHOLE_MASS } from '@/physics/Blackhole.ts';
import { LensingRenderer, type LensingRenderConfig } from '@/physics/LensingRenderer.ts';

// 关卡17特定配置
interface Level17SpecificConfig {
  /** 黑洞质量 */
  blackholeMass: number;
  /** 黑洞位置 x */
  blackholeX: number;
  /** 黑洞位置 y */
  blackholeY: number;
  /** 光源初始位置 x */
  lightSourceX: number;
  /** 光源初始位置 y */
  lightSourceY: number;
  /** 目标区域 x */
  targetX: number;
  /** 目标区域 y */
  targetY: number;
  /** 目标区域半径 */
  targetRadius: number;
  /** 可调整的光源角度范围 */
  angleRange: number;
  /** 公式 */
  formula: string;
  /** 提示 */
  hint: string;
  /** 成功条件：需要击中目标的逃逸光线数量 */
  minEscapedRays: number;
}

// 游戏状态
type GameState = 'idle' | 'running' | 'success' | 'failed';

// 游戏尺寸常量
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

// 渲染配置
const LENSING_CONFIG: LensingRenderConfig = {
  rayCount: 24,
  stepSize: 5,
  maxRayLength: 400,
  lineWidth: 2,
  lightRayColor: 0xffd700,
  capturedRayColor: 0xff4444,
  escapedRayColor: 0x4488ff,
  offsetY: 300,
  scale: 1,
  animationSpeed: 0.02,
};

/**
 * Level17Scene 类
 * 关卡17场景，实现引力透镜教学
 */
export class Level17Scene extends Phaser.Scene {
  // 关卡配置
  private levelConfig: LevelConfig | null = null;
  private levelSpecific: Level17SpecificConfig | null = null;

  // 游戏状态
  private gameState: GameState = 'idle';
  private elapsedTime: number = 0;

  // 物理模块
  private blackhole: Blackhole | null = null;
  private lensingRenderer: LensingRenderer | null = null;

  // 光源属性
  private lightSourcePosition: Vec2;
  private lightSourceAngle: number = 0;

  // 目标区域
  private targetZoneGraphics: Phaser.GameObjects.Graphics | null = null;

  // UI 元素
  private hintText: Phaser.GameObjects.Text | null = null;
  private angleSlider: Phaser.GameObjects.Graphics | null = null;
  private angleSliderHandle: Phaser.GameObjects.Graphics | null = null;
  private startButton: Phaser.GameObjects.Graphics | null = null;
  private resultText: Phaser.GameObjects.Text | null = null;
  private deflectionAngleText: Phaser.GameObjects.Text | null = null;
  private einsteinRingText: Phaser.GameObjects.Text | null = null;
  private capturedRayCountText: Phaser.GameObjects.Text | null = null;
  private escapedRayCountText: Phaser.GameObjects.Text | null = null;

  // 教程组件
  private tutorialUI: TutorialUI | null = null;

  // 滑块状态
  private sliderDragging: boolean = false;
  private sliderMinX: number = 200;
  private sliderMaxX: number = 600;
  private sliderY: number = 500;

  constructor() {
    super({ key: 'Level17Scene' });
    this.lightSourcePosition = Vec2(100, 0);
  }

  preload(): void {
    // 加载关卡配置
    this.load.json('level-17', 'src/levels/levels/level-17.json');
  }

  create(): void {
    // 加载关卡配置
    this.levelConfig = this.cache.json.get('level-17') as LevelConfig;

    if (this.levelConfig && this.levelConfig.levelSpecific) {
      this.levelSpecific = this.levelConfig.levelSpecific as unknown as Level17SpecificConfig;
    }

    // 创建背景
    this.createBackground();

    // 初始化黑洞和透镜渲染器
    this.initializeBlackhole();

    // 创建目标区域
    this.createTargetZone();

    // 创建 UI
    this.createUI();

    // 设置输入事件
    this.setupInputEvents();

    // 显示教程
    this.showTutorial();

    // 初始化数据显示
    this.updateDataDisplay();
  }

  override update(time: number, delta: number): void {
    if (this.gameState === 'running') {
      // 更新时间
      this.elapsedTime += delta / 1000;

      // 渲染引力透镜效果
      if (this.lensingRenderer) {
        this.lensingRenderer.render(time);
      }

      // 检测胜利条件
      this.checkGameEnd();
    } else if (this.gameState === 'idle') {
      // 预览模式：实时渲染
      if (this.lensingRenderer) {
        this.lensingRenderer.render(time);
      }
    }
  }

  /**
   * 创建背景
   */
  private createBackground(): void {
    // 宇宙深空背景
    this.cameras.main.setBackgroundColor('#0a0a1a');

    // 添加星空效果
    const starField = this.add.graphics();
    for (let i = 0; i < 80; i++) {
      const x = Math.random() * GAME_WIDTH;
      const y = Math.random() * GAME_HEIGHT;
      const size = Math.random() * 2 + 0.5;
      const brightness = Math.random() * 0.5 + 0.3;

      starField.fillStyle(0xffffff, brightness);
      starField.fillCircle(x, y, size);
    }

    // 添加网格线作为参考（淡化）
    const gridGraphics = this.add.graphics();
    gridGraphics.lineStyle(1, 0x1a1a3a, 0.15);

    // 垂直网格线
    for (let x = 0; x <= GAME_WIDTH; x += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(x, 0);
      gridGraphics.lineTo(x, GAME_HEIGHT);
      gridGraphics.strokePath();
    }

    // 水平网格线
    for (let y = 0; y <= GAME_HEIGHT; y += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(0, y);
      gridGraphics.lineTo(GAME_WIDTH, y);
      gridGraphics.strokePath();
    }
  }

  /**
   * 初始化黑洞和透镜渲染器
   */
  private initializeBlackhole(): void {
    if (!this.levelSpecific) {
      // 默认配置
      this.levelSpecific = {
        blackholeMass: DEFAULT_BLACKHOLE_MASS,
        blackholeX: 400,
        blackholeY: 300,
        lightSourceX: 100,
        lightSourceY: 300,
        targetX: 700,
        targetY: 300,
        targetRadius: 50,
        angleRange: Math.PI * 0.6,
        formula: 'δ ≈ 4GM/(rc²) = 2Rs/r',
        hint: '调整光线方向，观察引力透镜效应',
        minEscapedRays: 5,
      };
    }

    // 创建黑洞实例
    this.blackhole = new Blackhole({
      id: 'level17-blackhole',
      position: Vec2(this.levelSpecific.blackholeX, this.levelSpecific.blackholeY),
      mass: this.levelSpecific.blackholeMass,
    });

    // 设置光源位置
    this.lightSourcePosition = Vec2(
      this.levelSpecific.lightSourceX,
      this.levelSpecific.lightSourceY
    );

    // 创建引力透镜渲染器
    this.lensingRenderer = new LensingRenderer(
      this,
      this.blackhole,
      LENSING_CONFIG,
      this.lightSourcePosition
    );
  }

  /**
   * 创建目标区域
   */
  private createTargetZone(): void {
    if (!this.levelSpecific) return;

    this.targetZoneGraphics = this.add.graphics();

    const targetX = this.levelSpecific.targetX;
    const targetY = this.levelSpecific.targetY;
    const radius = this.levelSpecific.targetRadius;

    // 绘制目标区域（半透明绿色圆）
    this.targetZoneGraphics.fillStyle(0x4ade80, 0.3);
    this.targetZoneGraphics.fillCircle(targetX, targetY, radius);

    // 绘制目标区域边框
    this.targetZoneGraphics.lineStyle(3, 0x4ade80, 1);
    this.targetZoneGraphics.strokeCircle(targetX, targetY, radius);

    // 添加标签
    this.add.text(targetX, targetY + radius + 15, '目标区域', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
  }

  /**
   * 创建 UI
   */
  private createUI(): void {
    // 标题
    this.add.text(GAME_WIDTH / 2, 30, '关卡17：引力透镜', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 公式显示
    this.add.text(GAME_WIDTH / 2, 55, '光线偏折角：δ ≈ 4GM/(rc²) = 2Rs/r', {
      fontSize: '16px',
      color: '#ffff00',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, 75, '爱因斯坦环：r_E = √(4GMd/c²)', {
      fontSize: '14px',
      color: '#ffff88',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 提示文本
    this.hintText = this.add.text(
      GAME_WIDTH / 2,
      100,
      this.levelSpecific?.hint ?? '调整光线方向，观察引力透镜效应',
      {
        fontSize: '14px',
        color: '#888888',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);

    // 数据显示面板
    this.createDataPanel();

    // 角度控制滑块
    this.createAngleSlider();

    // 开始按钮
    this.createStartButton();

    // 结果文本（初始隐藏）
    this.resultText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 150, '', {
      fontSize: '24px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5).setVisible(false);

    // 物理说明区域
    this.createPhysicsInfo();
  }

  /**
   * 创建数据显示面板
   */
  private createDataPanel(): void {
    const panelX = 50;
    const panelY = 120;
    const panelWidth = 200;
    const panelHeight = 150;

    // 面板背景
    const panel = this.add.graphics();
    panel.fillStyle(0x1a1a3a, 0.8);
    panel.fillRect(panelX, panelY, panelWidth, panelHeight);
    panel.lineStyle(1, 0x4a4a6a);
    panel.strokeRect(panelX, panelY, panelWidth, panelHeight);

    // 偏折角显示
    this.add.text(panelX + 10, panelY + 10, '平均偏折角:', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    this.deflectionAngleText = this.add.text(
      panelX + panelWidth - 10,
      panelY + 10,
      '--',
      {
        fontSize: '12px',
        color: '#ffd700',
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }
    ).setOrigin(1, 0);

    // 爱因斯坦环半径
    this.add.text(panelX + 10, panelY + 35, '爱因斯坦环半径:', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    this.einsteinRingText = this.add.text(
      panelX + panelWidth - 10,
      panelY + 35,
      '--',
      {
        fontSize: '12px',
        color: '#ffff88',
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }
    ).setOrigin(1, 0);

    // 被捕获光线数量
    this.add.text(panelX + 10, panelY + 60, '被捕获光线:', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    this.capturedRayCountText = this.add.text(
      panelX + panelWidth - 10,
      panelY + 60,
      '--',
      {
        fontSize: '12px',
        color: '#ff4444',
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }
    ).setOrigin(1, 0);

    // 逃逸光线数量
    this.add.text(panelX + 10, panelY + 85, '逃逸光线:', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    this.escapedRayCountText = this.add.text(
      panelX + panelWidth - 10,
      panelY + 85,
      '--',
      {
        fontSize: '12px',
        color: '#4488ff',
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }
    ).setOrigin(1, 0);

    // 黑洞质量
    this.add.text(panelX + 10, panelY + 110, `黑洞质量: ${this.blackhole?.getMass() ?? '--'}`, {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    // 史瓦西半径
    this.add.text(
      panelX + 10,
      panelY + 130,
      `史瓦西半径: ${this.blackhole?.getSchwarzschildRadius().toFixed(2) ?? '--'}`,
      {
        fontSize: '12px',
        color: '#888888',
        fontFamily: 'Arial',
      }
    );
  }

  /**
   * 创建角度控制滑块
   */
  private createAngleSlider(): void {
    const y = this.sliderY;

    // 滑块轨道背景
    this.angleSlider = this.add.graphics();
    this.angleSlider.fillStyle(0x4a4a6a, 0.8);
    this.angleSlider.fillRect(this.sliderMinX, y - 10, this.sliderMaxX - this.sliderMinX, 20);
    this.angleSlider.lineStyle(1, 0x6a6a8a);
    this.angleSlider.strokeRect(this.sliderMinX, y - 10, this.sliderMaxX - this.sliderMinX, 20);

    // 角度标记
    this.add.text(this.sliderMinX, y + 25, '-90°', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.add.text(this.sliderMaxX, y + 25, '+90°', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.add.text((this.sliderMinX + this.sliderMaxX) / 2, y + 25, '0°', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 滑块提示
    this.add.text(GAME_WIDTH / 2, y - 30, '调整光线发射角度:', {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 滑块把手
    this.angleSliderHandle = this.add.graphics();
    const handleX = (this.sliderMinX + this.sliderMaxX) / 2;
    this.drawSliderHandle(handleX, y, false);

    // 设置滑块交互
    this.angleSlider.setInteractive(
      new Phaser.Geom.Rectangle(this.sliderMinX, y - 15, this.sliderMaxX - this.sliderMinX, 30),
      Phaser.Geom.Rectangle.Contains
    );

    this.angleSlider.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.gameState === 'idle') {
        this.sliderDragging = true;
        this.updateSliderPosition(pointer.x);
      }
    });

    this.angleSlider.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.sliderDragging && this.gameState === 'idle') {
        this.updateSliderPosition(pointer.x);
      }
    });

    this.angleSlider.on('pointerup', () => {
      this.sliderDragging = false;
    });

    this.angleSlider.on('pointerout', () => {
      this.sliderDragging = false;
    });
  }

  /**
   * 绘制滑块把手
   */
  private drawSliderHandle(x: number, y: number, hovered: boolean): void {
    if (!this.angleSliderHandle) return;

    this.angleSliderHandle.clear();
    this.angleSliderHandle.fillStyle(hovered ? 0x4ade80 : 0x00aaff, 1);
    this.angleSliderHandle.fillCircle(x, y, 12);
    this.angleSliderHandle.lineStyle(2, 0xffffff, 0.8);
    this.angleSliderHandle.strokeCircle(x, y, 12);
  }

  /**
   * 更新滑块位置
   */
  private updateSliderPosition(pointerX: number): void {
    // 限制在滑块范围内
    const clampedX = Math.max(this.sliderMinX, Math.min(this.sliderMaxX, pointerX));

    // 计算角度（-90° 到 +90°）
    const normalizedX = (clampedX - this.sliderMinX) / (this.sliderMaxX - this.sliderMinX);
    this.lightSourceAngle = (normalizedX - 0.5) * Math.PI; // -π/2 到 +π/2

    // 更新把手位置
    this.drawSliderHandle(clampedX, this.sliderY, false);

    // 更新光源角度
    this.updateLightSourceAngle();
  }

  /**
   * 更新光源发射角度
   */
  private updateLightSourceAngle(): void {
    if (!this.lensingRenderer) return;

    // 更新渲染器配置（重新初始化光线）
    this.lensingRenderer.setLightSourcePosition(this.lightSourcePosition);

    // 更新数据显示
    this.updateDataDisplay();
  }

  /**
   * 创建开始按钮
   */
  private createStartButton(): void {
    this.startButton = this.add.graphics();
    this.startButton.fillStyle(0x4ade80, 1);
    this.startButton.fillRoundedRect(GAME_WIDTH / 2 - 60, 530, 120, 45, 8);
    this.startButton.lineStyle(2, 0x4ade80);
    this.startButton.strokeRoundedRect(GAME_WIDTH / 2 - 60, 530, 120, 45, 8);

    this.add.text(GAME_WIDTH / 2, 552, '发射光线', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.startButton.setInteractive(
      new Phaser.Geom.Rectangle(GAME_WIDTH / 2 - 60, 530, 120, 45),
      Phaser.Geom.Rectangle.Contains
    );

    this.startButton.on('pointerdown', () => this.startGame());

    this.startButton.on('pointerover', () => {
      if (this.gameState === 'idle') {
        this.startButton?.clear();
        this.startButton?.fillStyle(0x5aee90, 1);
        this.startButton?.fillRoundedRect(GAME_WIDTH / 2 - 60, 530, 120, 45, 8);
        this.startButton?.strokeRoundedRect(GAME_WIDTH / 2 - 60, 530, 120, 45, 8);
      }
    });

    this.startButton.on('pointerout', () => {
      if (this.gameState === 'idle') {
        this.startButton?.clear();
        this.startButton?.fillStyle(0x4ade80, 1);
        this.startButton?.fillRoundedRect(GAME_WIDTH / 2 - 60, 530, 120, 45, 8);
        this.startButton?.strokeRoundedRect(GAME_WIDTH / 2 - 60, 530, 120, 45, 8);
      }
    });
  }

  /**
   * 创建物理信息显示
   */
  private createPhysicsInfo(): void {
    // 物理公式说明
    this.add.text(550, 120, '引力透镜原理:', {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    });

    this.add.text(550, 145, '• 光线经过黑洞附近时会被弯曲', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    this.add.text(550, 165, '• 偏折角与距离成正比：δ = 2Rs/r', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    this.add.text(550, 185, '• 爱因斯坦环：光源、黑洞、观测者对齐', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    this.add.text(550, 205, '• 天文学应用：发现暗物质、测量星系质量', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });
  }

  /**
   * 更新数据显示
   */
  private updateDataDisplay(): void {
    if (!this.lensingRenderer || !this.blackhole) return;

    // 平均偏折角
    const avgDeflection = this.lensingRenderer.getAverageDeflectionAngle();
    if (avgDeflection === Infinity) {
      this.deflectionAngleText?.setText('∞');
    } else {
      const degrees = avgDeflection * (180 / Math.PI);
      this.deflectionAngleText?.setText(`${degrees.toFixed(2)}°`);
    }

    // 爱因斯坦环半径（简化计算）
    const rs = this.blackhole.getSchwarzschildRadius();
    const einsteinRadius = Math.sqrt(rs * 2) * LENSING_CONFIG.scale!;
    this.einsteinRingText?.setText(`${einsteinRadius.toFixed(2)} 单位`);

    // 被捕获光线数量
    const capturedCount = this.lensingRenderer.getCapturedRayCount();
    this.capturedRayCountText?.setText(`${capturedCount}`);

    // 逃逸光线数量
    const escapedCount = this.lensingRenderer.getEscapedRayCount();
    this.escapedRayCountText?.setText(`${escapedCount}`);
  }

  /**
   * 开始游戏
   */
  private startGame(): void {
    if (this.gameState !== 'idle') return;

    this.gameState = 'running';
    this.elapsedTime = 0;

    // 更新按钮状态
    this.updateStartButtonState('running');

    // 更新提示
    this.hintText?.setText('光线正在穿越黑洞引力场...');

    // 自动完成计时器（3秒后检查）
    this.time.delayedCall(3000, () => {
      this.checkGameEnd();
    });
  }

  /**
   * 检测游戏结束
   */
  private checkGameEnd(): void {
    if (this.gameState !== 'running') return;

    if (!this.lensingRenderer || !this.levelSpecific) return;

    // 检查逃逸光线数量是否达标
    const escapedCount = this.lensingRenderer.getEscapedRayCount();

    // 检查是否有光线击中目标区域
    const hitTarget = this.checkRaysHitTarget();

    if (escapedCount >= this.levelSpecific.minEscapedRays || hitTarget) {
      this.gameState = 'success';
      this.showSuccessResult();
    } else {
      // 即使不达标，也显示部分成功（教学关卡）
      this.gameState = 'success';
      this.showSuccessResult();
    }

    this.updateStartButtonState('idle');
  }

  /**
   * 检查光线是否击中目标区域
   */
  private checkRaysHitTarget(): boolean {
    if (!this.lensingRenderer || !this.levelSpecific || !this.blackhole) return false;

    const results = this.lensingRenderer.getLightRayResults();
    const targetX = this.levelSpecific.targetX;
    const targetY = this.levelSpecific.targetY;
    const targetRadius = this.levelSpecific.targetRadius;

    // 检查每条逃逸光线的终点
    for (const result of results) {
      if (result.escaped && result.path.length > 0) {
        const lastPoint = result.path[result.path.length - 1];
        if (lastPoint) {
          // 转换为屏幕坐标
          const screenX = lastPoint.x * LENSING_CONFIG.scale!;
          const screenY = LENSING_CONFIG.offsetY! - lastPoint.y * LENSING_CONFIG.scale!;

          // 检查是否在目标区域范围内
          const dx = screenX - targetX;
          const dy = screenY - targetY;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance <= targetRadius) {
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * 更新开始按钮状态
   */
  private updateStartButtonState(state: GameState): void {
    if (!this.startButton) return;

    this.startButton.clear();

    if (state === 'running') {
      this.startButton.fillStyle(0x6a6a8a, 1);
    } else {
      this.startButton.fillStyle(0x4ade80, 1);
    }
    this.startButton.fillRoundedRect(GAME_WIDTH / 2 - 60, 530, 120, 45, 8);
    this.startButton.strokeRoundedRect(GAME_WIDTH / 2 - 60, 530, 120, 45, 8);
  }

  /**
   * 显示成功结果
   */
  private showSuccessResult(): void {
    const gameState = getGameState();
    gameState.completeLevel(17, this.elapsedTime);

    if (!this.lensingRenderer) return;

    const escapedCount = this.lensingRenderer.getEscapedRayCount();
    const capturedCount = this.lensingRenderer.getCapturedRayCount();
    const avgDeflection = this.lensingRenderer.getAverageDeflectionAngle();

    let deflectionDegrees = '∞';
    if (avgDeflection !== Infinity) {
      deflectionDegrees = `${(avgDeflection * (180 / Math.PI)).toFixed(2)}°`;
    }

    this.resultText?.setText(
      `✅ 成功！逃逸光线: ${escapedCount}, 捕获光线: ${capturedCount}`
    )
      .setColor('#4ade80')
      .setVisible(true);

    this.hintText?.setText(
      `平均偏折角: ${deflectionDegrees} — 引力透镜效应解释了宇宙中的光线弯曲现象！`
    );

    this.createCompletionButtons();
  }

  /**
   * 创建完成后的按钮
   */
  private createCompletionButtons(): void {
    const y = GAME_HEIGHT / 2 + 200;

    const nextBtn = this.add.text(GAME_WIDTH / 2 - 80, y, '下一关', {
      fontSize: '16px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    nextBtn.on('pointerdown', () => {
      const gameState = getGameState();
      if (gameState.isLevelUnlocked(18)) {
        this.scene.start('Level18Scene');
      } else {
        this.scene.start('LevelSelectScene');
      }
    });

    const retryBtn = this.add.text(GAME_WIDTH / 2, y, '重试', {
      fontSize: '16px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    retryBtn.on('pointerdown', () => {
      this.resetGame();
    });

    const backBtn = this.add.text(GAME_WIDTH / 2 + 80, y, '返回', {
      fontSize: '16px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });
  }

  /**
   * 重置游戏
   */
  private resetGame(): void {
    this.gameState = 'idle';
    this.elapsedTime = 0;
    this.lightSourceAngle = 0;

    // 重置滑块位置
    const handleX = (this.sliderMinX + this.sliderMaxX) / 2;
    this.drawSliderHandle(handleX, this.sliderY, false);

    // 更新光源角度
    this.updateLightSourceAngle();

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 重置提示
    this.hintText?.setText(this.levelSpecific?.hint ?? '调整光线方向，观察引力透镜效应');

    // 更新按钮状态
    this.updateStartButtonState('idle');

    // 更新数据显示
    this.updateDataDisplay();
  }

  /**
   * 设置输入事件
   */
  private setupInputEvents(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.gameState !== 'idle') return;

      if (event.key === 'Enter') {
        this.startGame();
      }

      if (event.key === 'r' || event.key === 'R') {
        this.resetGame();
      }

      // 左右箭头调整角度
      if (event.key === 'ArrowLeft') {
        this.lightSourceAngle -= 0.1;
        this.updateSliderFromAngle();
      }

      if (event.key === 'ArrowRight') {
        this.lightSourceAngle += 0.1;
        this.updateSliderFromAngle();
      }
    });
  }

  /**
   * 根据角度更新滑块位置
   */
  private updateSliderFromAngle(): void {
    // 限制角度范围
    this.lightSourceAngle = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.lightSourceAngle));

    // 计算滑块位置
    const normalizedAngle = (this.lightSourceAngle + Math.PI / 2) / Math.PI;
    const handleX = this.sliderMinX + normalizedAngle * (this.sliderMaxX - this.sliderMinX);

    // 更新把手位置
    this.drawSliderHandle(handleX, this.sliderY, false);

    // 更新光源角度
    this.updateLightSourceAngle();
  }

  /**
   * 显示教程
   */
  private showTutorial(): void {
    const tutorialData = getTutorial('level-17');
    if (!tutorialData) return;

    this.tutorialUI = new TutorialUI(this, {
      levelNumber: 17,
      tutorialData,
      onStart: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
      onSkip: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
    });
  }

  /**
   * 获取当前游戏状态
   */
  getGameState(): GameState {
    return this.gameState;
  }

  /**
   * 获取当前时间
   */
  getElapsedTime(): number {
    return this.elapsedTime;
  }

  /**
   * 获取黑洞实例
   */
  getBlackhole(): Blackhole | null {
    return this.blackhole;
  }

  /**
   * 获取透镜渲染器
   */
  getLensingRenderer(): LensingRenderer | null {
    return this.lensingRenderer;
  }

  /**
   * 获取当前光线角度
   */
  getLightSourceAngle(): number {
    return this.lightSourceAngle;
  }
}